/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import {
  daySystemLocalEpoch,
  MaxTime,
  SimulatorAt,
  UnsafeSimulator
} from '@ki-wi/deterministic';

import { IteratorFactory } from './iterator-factory';

/**
 * @import { SpotContent, Spot } from '.'
 * @import { Playlist } from '../../playlist/playlist'
 * @import UnixTimestamp from '../../unix-timestamp/unix-timestamp'
 * @import { Time } from '@ki-wi/deterministic';
 */

export class RealtimePlaylistSimulator {
  /**
   * @param {boolean} disableAutoRewind
   * @param {() => Time} nowNs
   * @param {Playlist} playlist
   * @param {number} parentVolume
   * @param {UnixTimestamp} unixTimestamp
   */
  constructor(disableAutoRewind, nowNs, playlist, parentVolume, unixTimestamp) {
    this.disableAutoRewind = disableAutoRewind;
    this.nowNs = nowNs;
    this.unixTimeOffset = unixTimestamp.offset();
    const clipIteratorFactory = IteratorFactory.fromPlaylist(
      playlist,
      parentVolume,
      { excludeUnknownClipDuration: false }
    );

    this.simulator = new UnsafeSimulator(clipIteratorFactory, nowNs(), MaxTime);
  }

  /**
   * @returns {Promise<Spot<SpotContent | null> | undefined>}
   */
  async next() {
    return (
      (await getNextSpot(this.nowNs, this.simulator, this.disableAutoRewind)) ??
      undefined
    );
  }

  /**
   * @returns {Promise<Spot<SpotContent | null> | undefined>}
   */
  async peek() {
    const s = this.simulator.clone();
    return (
      (await getNextSpot(this.nowNs, s, this.disableAutoRewind)) ?? undefined
    );
  }
}

/**
 * @param {() => Time} nowNs
 * @param {UnsafeSimulator<SpotContent, string>} simulator
 * @param {boolean} disableAutoRewind
 * @returns {Spot<SpotContent | null> | null}
 */
function getNextSpot(nowNs, simulator, disableAutoRewind) {
  simulator.time = nowNs();
  let spot = simulator.next();
  // disableAutoRewind === true - konečný playlist
  if (disableAutoRewind) {
    return spot;
  }
  // disableAutoRewind === false - nekonečný playlist, pokud přijde zarážka, tak zkusím další volání next
  if (spot && spot.content === null && spot.duration === 0) {
    // Čas se neposouvá před voláním next, protože byla detekována zarážka.
    spot = simulator.next();
  }
  return spot;
}

export class DeterministicPlaylistSimulator {
  /**
   * @param {Playlist} playlist
   * @param {number} parentVolume
   * @param {UnixTimestamp} unixTimestamp
   */
  constructor(playlist, parentVolume, unixTimestamp) {
    this.unixTimestamp = unixTimestamp;
    this.unixTimeOffset = unixTimestamp.offset();

    this.simulatorAt = new SimulatorAt(
      IteratorFactory.fromPlaylist(playlist, parentVolume, {
        excludeUnknownClipDuration: true
      }),
      daySystemLocalEpoch
    );
    this.now = this.now.bind(this);
  }

  /**
   * @returns {Promise<Spot<SpotContent | null> | undefined>}
   */
  async next() {
    this.unixTimeOffset = this.unixTimestamp.offset();
    const time = this.now();
    const spot = (await this.simulatorAt.getAt(time)) ?? undefined;
    return spot;
  }

  /**
   * @param {Spot} spot
   * TODO: vyřešit nutnost předávání spot. Získání aktuálního spotu z next
   * metody způsobí neočekávané přehrávní.
   * @returns {Promise<Spot<SpotContent | null> | undefined>}
   */
  async peek(spot) {
    const time = /** @type {Time} */ (spot.time + spot.duration);
    return (await this.simulatorAt.getAt(time)) ?? undefined;
  }

  /**
   * Zjistí aktuální čas.
   * @private
   * @returns {Time} Aktuální čas v mikrosekundách.
   */
  now() {
    return /** @type {Time} */ (
      (this.unixTimestamp.now() + this.unixTimeOffset) * 1000
    );
  }
}

export class SimulatorUnrecoverableError extends Error {
  /**
   * @param {string} [message]
   * @param {ErrorOptions} [options]
   */
  constructor(message, options) {
    super(message, options);
    this.name = 'SimulatorUnrecoverableError';
  }
}
